import {
    NamedProperty,
    deduplicateByProperty,
    getSortedCollection,
    NumberedProperty,
    ensure,
    mapToNamedProperty
} from "sprint-common-web";

import { TvSeriesAssetsListEpisodeItem } from "../../common/AssetItem";
import { VersionDetails } from "../context/SingleTvSeriesContext";
import { EpisodeVersionCatalogueEntry, TvSeriesCatalogueEntry } from "../context/TvSeriesCatalogueEntry";

export const useSingleTvSeriesDetails = () => {
    const getSeasons = (tvSeries?: TvSeriesCatalogueEntry): NumberedProperty[] => {
        if (!tvSeries) {
            return [];
        }

        const seasons = tvSeries.seasons.map<NumberedProperty>((season) => ({
            id: season.id,
            name: season.name,
            number: season.number
        }));

        return getSortedCollection(seasons, "number", "asc");
    };

    const getEpisodes = (tvSeries?: TvSeriesCatalogueEntry, seasonId?: string): TvSeriesAssetsListEpisodeItem[] => {
        if (!tvSeries) {
            return [];
        }

        const filteredSeasons = seasonId
            ? tvSeries.seasons.filter((season) => season.id === seasonId)
            : tvSeries.seasons;

        const episodes = filteredSeasons.flatMap((season) => season.episodes);

        return getSortedCollection(episodes, "number", "asc");
    };

    const getVersionDetails = (
        tvSeries?: TvSeriesCatalogueEntry,
        seasonId?: string,
        episodeId?: string
    ): VersionDetails => {
        const filteredEpisodeVersion = getEpisodeVersions(tvSeries, seasonId, episodeId);

        const versionTypes = getVersionTypes(filteredEpisodeVersion).sort(sortByName);
        const distributors = getDistributors(filteredEpisodeVersion).sort(sortByName);
        const brandings = getBrandings(filteredEpisodeVersion).sort(sortByName);

        return { versionTypes, distributors, brandings };
    };

    return {
        getSeasons,
        getEpisodes,
        getVersionDetails
    };
};

const getVersionTypes = (episodeVersions: EpisodeVersionCatalogueEntry[]): NamedProperty[] => {
    const versionTypes = episodeVersions.map((version) => mapToNamedProperty(version.type));

    return deduplicateByProperty(versionTypes, "id");
};

const getDistributors = (episodeVersions: EpisodeVersionCatalogueEntry[]): NamedProperty[] => {
    const distributors = episodeVersions.map((version) => mapToNamedProperty(version.distributor));

    return deduplicateByProperty(distributors, "id");
};

const getBrandings = (episodeVersions: EpisodeVersionCatalogueEntry[]): NamedProperty[] => {
    const filteredEpisodeVersion = episodeVersions.filter((version) => !!version.brandings?.length);

    const brandings = filteredEpisodeVersion
        .flatMap((version) => {
            ensure(version.brandings);

            return version.brandings;
        })
        .map((branding) => mapToNamedProperty(branding));

    return deduplicateByProperty(brandings, "id");
};

const getEpisodeVersions = (
    tvSeries?: TvSeriesCatalogueEntry,
    seasonId?: string,
    episodeId?: string
): EpisodeVersionCatalogueEntry[] => {
    if (!tvSeries) {
        return [];
    }

    const filteredSeasons = seasonId ? tvSeries.seasons.filter((season) => season.id === seasonId) : tvSeries.seasons;

    const episodes = filteredSeasons.flatMap((season) => season.episodes);

    const filteredEpisodes = episodeId ? episodes.filter((episode) => episode.id === episodeId) : episodes;

    return filteredEpisodes.flatMap((episode) => episode.versions);
};

const sortByName = (a: { name: string }, b: { name: string }) => (a.name >= b.name ? 1 : -1);
